// Shared tool marks: the connector logos (ConnectorsSection) and the small
// "which tool did this" icon on each Audit Logs row.
import React from "react";
import asanaLogo from "../images/asana.svg";
import jiraLogo from "../images/jira.svg";
import slackLogo from "../images/slack.svg";
import teamsLogo from "../images/microsoftteams.svg";
import outlookLogo from "../images/outlook.svg";
import googleCalendarLogo from "../images/google-calendar.svg";
import hubspotLogo from "../images/hubspot.png";
// Microsoft's current (2024) Planner icon, public domain via Wikimedia Commons.
import plannerLogo from "../images/microsoft-planner.svg";
import bossdropMark from "../images/bossdrop_logo_transparent_512.png";
import "./ToolIcon.css";

// Linear and GitHub have no image in src/images, so their marks are inline
// SVG rather than new binaries. GitHub's is currentColor so it stays visible in
// both themes; Linear's brand indigo reads on either background.
export function LinearLogo() {
  return (
    <svg
      className="toolLogo"
      viewBox="0 0 100 100"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="#5E6AD2"
        d="M1.22541 61.5228c-.2225-.9485.90748-1.5459 1.59638-.857L39.3342 97.1782c.6889.6889.0915 1.8189-.857 1.5964C20.0515 94.4522 5.54779 79.9485 1.22541 61.5228ZM.00189135 46.8891c-.01764375.2833.08887215.5599.28957165.7606L52.3503 99.7085c.2007.2007.4773.3075.7606.2896 2.3692-.1476 4.6938-.46 6.9624-.9259.7645-.157 1.0301-1.0963.4782-1.6481L2.57595 39.4485c-.55186-.5519-1.49117-.2863-1.648174.4782-.465915 2.2686-.77832 4.5932-.92588465 6.9624ZM4.21093 29.7054c-.16649.3738-.08169.8106.20765 1.1l64.77602 64.776c.2894.2894.7262.3742 1.1.2077 1.7861-.7956 3.5171-1.6927 5.1855-2.684.5521-.328.6373-1.0867.1832-1.5407L8.43566 24.3367c-.45409-.4541-1.21271-.3689-1.54074.1832-.99132 1.6684-1.88843 3.3994-2.68399 5.1855ZM12.6587 18.074c-.3701-.3701-.393-.9637-.0443-1.3541C21.7795 6.45931 35.1114 0 49.9519 0 77.5927 0 100 22.4073 100 50.0481c0 14.8405-6.4593 28.1724-16.7199 37.3375-.3903.3487-.984.3258-1.3542-.0443L12.6587 18.074Z"
      />
    </svg>
  );
}

export function GitHubLogo() {
  return (
    <svg
      className="toolLogo"
      viewBox="0 0 16 16"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="currentColor"
        d="M8 0c4.42 0 8 3.58 8 8a8.013 8.013 0 0 1-5.45 7.59c-.4.08-.55-.17-.55-.38 0-.27.01-1.13.01-2.2 0-.75-.25-1.23-.54-1.48 1.78-.2 3.65-.88 3.65-3.95 0-.88-.31-1.59-.82-2.15.08-.2.36-1.02-.08-2.12 0 0-.67-.22-2.2.82-.64-.18-1.32-.27-2-.27-.68 0-1.36.09-2 .27-1.53-1.03-2.2-.82-2.2-.82-.44 1.1-.16 1.92-.08 2.12-.51.56-.82 1.28-.82 2.15 0 3.06 1.86 3.75 3.64 3.95-.23.2-.44.55-.51 1.07-.46.21-1.61.55-2.33-.66-.15-.24-.6-.83-1.23-.82-.67.01-.27.38.01.53.34.19.73.9.82 1.13.16.45.68 1.31 2.69.94 0 .67.01 1.3.01 1.49 0 .21-.15.45-.55.38A7.995 7.995 0 0 1 0 8c0-4.42 3.58-8 8-8Z"
      />
    </svg>
  );
}

// ClickUp, Notion and monday.com have no image in src/images either. ClickUp's
// shape is the simple-icons mark (CC0) in two ClickUp brand colors. Notion's
// mark is black and white, so it is drawn in currentColor to read in both
// themes. monday.com's is its three colored shapes as simple strokes.
export function ClickUpLogo() {
  return (
    <svg
      className="toolLogo"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="#FF02F0"
        d="M2 18.439l3.69-2.828c1.961 2.56 4.044 3.739 6.363 3.739 2.307 0 4.33-1.166 6.203-3.704L22 18.405C19.298 22.065 15.941 24 12.053 24 8.178 24 4.788 22.078 2 18.439z"
      />
      <path
        fill="#8930FD"
        d="M12.04 6.15l-6.568 5.66-3.036-3.52L12.055 0l9.543 8.296-3.05 3.509z"
      />
    </svg>
  );
}

export function NotionLogo() {
  return (
    <svg
      className="toolLogo"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <rect
        x="3.2"
        y="3.2"
        width="17.6"
        height="17.6"
        rx="3.6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
      />
      <path
        d="M8.6 16.4V7.6l6.8 8.8V7.6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function MondayLogo() {
  return (
    <svg
      className="toolLogo"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <g fill="none" strokeLinecap="round" strokeWidth="4.2">
        <path stroke="#FF3D57" d="M3.6 16.6 7.6 8.4" />
        <path stroke="#FFCB00" d="M10.4 16.6 14.4 8.4" />
      </g>
      <circle cx="19.6" cy="15.9" r="2.5" fill="#00CA72" />
    </svg>
  );
}

// Microsoft Planner's own mark (src/images/microsoft-planner.svg).
export function PlannerLogo() {
  return <ImgLogo src={plannerLogo} />;
}

// Email that BossDrop sends goes out from its own mailbox (ai@bossdrop.ai),
// not the customer's Outlook or Gmail, so it gets a plain envelope, not a
// mail client's logo. currentColor keeps it readable in both themes.
export function MailGlyph() {
  return (
    <svg
      className="toolLogo"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <rect
        x="3"
        y="5.5"
        width="18"
        height="13"
        rx="2.6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
      />
      <path
        d="m4.2 7.4 7.8 5.7 7.8-5.7"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ImgLogo({ src }) {
  return <img src={src} alt="" className="toolLogo" />;
}

function BossDropMark() {
  return (
    <span className="toolLogo toolIcon__bossdrop" aria-hidden="true">
      <img src={bossdropMark} alt="" />
    </span>
  );
}

// Pipedrive has no image in src/images: its letter on its green.
export function PipedriveLogo() {
  return (
    <svg className="toolLogo" viewBox="0 0 32 32" aria-hidden="true" focusable="false">
      <rect width="32" height="32" rx="7" fill="#017737" />
      <path
        fill="#fff"
        d="M17.2 7.5c-2.2 0-3.5 1-4.1 1.7l-.1-1.4H9v16.9h4.1v-5.3c.7.6 1.9 1 3.3 1 3.7 0 6.6-2.9 6.6-6.5 0-3.9-2.3-6.4-5.8-6.4zm-.9 9.5c-2 0-3.3-1.4-3.3-3.1 0-2 1.5-3.1 3.2-3.1 1.8 0 3.1 1.3 3.1 3.1 0 2-1.4 3.1-3 3.1z"
      />
    </svg>
  );
}

// key -> label + mark. The keys are the ones src/lib/auditLogs.js derives
// from a log's own fields (meta.backend, meta.chatBackend, meta.provider...).
export const TOOL_ICONS = {
  outlook: { label: "Outlook", render: () => <ImgLogo src={outlookLogo} /> },
  teams: { label: "Teams", render: () => <ImgLogo src={teamsLogo} /> },
  slack: { label: "Slack", render: () => <ImgLogo src={slackLogo} /> },
  email: { label: "Email", render: () => <MailGlyph /> },
  google: {
    label: "Google Calendar",
    render: () => <ImgLogo src={googleCalendarLogo} />,
  },
  asana: { label: "Asana", render: () => <ImgLogo src={asanaLogo} /> },
  jira: { label: "Jira", render: () => <ImgLogo src={jiraLogo} /> },
  linear: { label: "Linear", render: () => <LinearLogo /> },
  clickup: { label: "ClickUp", render: () => <ClickUpLogo /> },
  notion: { label: "Notion", render: () => <NotionLogo /> },
  monday: { label: "monday.com", render: () => <MondayLogo /> },
  planner: { label: "Planner", render: () => <PlannerLogo /> },
  github: { label: "GitHub", render: () => <GitHubLogo /> },
  hubspot: { label: "HubSpot", render: () => <ImgLogo src={hubspotLogo} /> },
  pipedrive: { label: "Pipedrive", render: () => <PipedriveLogo /> },
  bossdrop: { label: "BossDrop", render: () => <BossDropMark /> },
};

/** The mark for one tool key, or null for a key with no mark. */
export default function ToolIcon({ tool, className = "", title }) {
  const entry = TOOL_ICONS[tool];
  if (!entry) return null;
  return (
    <span
      className={`toolIcon ${className}`.trim()}
      title={title || entry.label}
      data-tool={tool}
    >
      {entry.render()}
    </span>
  );
}
